/**
 * «Что делает Claude» в Автоконтенте (§3.4): панель на каждую идущую задачу — и по кнопке, и по расписанию.
 * Свёрнутая — текущий шаг и время; раскрытая — живая лента (SSE): поиски, страницы, мысли, картинки.
 * После завершения — итог и ссылки на созданные посты; через несколько минут панель уходит сама.
 */
import { useCallback, useEffect, useId, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router";
import { ChevronDown, CircleCheck, FileText, RotateCcw, TriangleAlert, X } from "lucide-react";
import type { JobDetail, JobSummary } from "@impuls/shared";
import { api, errText } from "../lib/api";
import { duration } from "../lib/format";
import { useJobStream } from "../lib/streams";
import { EcgLoader } from "./Ecg";
import { JobFeed, useElapsed } from "./JobFeed";
import { StatusChip } from "./PostBits";
import { useToast } from "./ui";

/* ---------- какие задачи показывать ---------- */
const STORE = "impuls.auto.jobs";
const KEEP_MS = 12 * 3600_000;
/** сколько держать панель завершённой задачи, если уйти со страницы и вернуться */
export const DONE_KEEP_MS = 10 * 60_000;
const MAX = 8;

interface Entry {
  id: number;
  at: number;
  /** мс: когда задача закончилась (видели здесь) */
  done?: number;
}

export function load(): Entry[] {
  try {
    const raw: unknown = JSON.parse(sessionStorage.getItem(STORE) ?? "[]");
    if (!Array.isArray(raw)) return [];
    const now = Date.now();
    return raw
      .filter(
        (e): e is Entry =>
          Number.isInteger(e?.id) &&
          typeof e?.at === "number" &&
          now - e.at < KEEP_MS &&
          (e.done === undefined || (typeof e.done === "number" && now - e.done < DONE_KEEP_MS)),
      )
      .slice(0, MAX);
  } catch {
    return [];
  }
}

function save(list: Entry[]) {
  try {
    sessionStorage.setItem(STORE, JSON.stringify(list));
  } catch {}
}

/**
 * Панели «Что делает Claude»: идущие задачи автоконтента (в том числе плановая подборка — её приносит
 * auto-status, который обновляет общий поток) и те, что закончились у вас на глазах в последние минуты.
 * Ничего не идёт — панелей нет.
 */
export function useAutoJobs(running: JobSummary[] | undefined) {
  const [list, setList] = useState<Entry[]>(load);
  // задачи, которые идут или шли, пока открыта страница — их панели раскрыты с живой лентой
  const [live, setLive] = useState<Set<number>>(() => new Set());
  useEffect(() => save(list), [list]);

  const add = useCallback((id: number) => {
    setList((xs) => (xs.some((x) => x.id === id) ? xs : [{ id, at: Date.now() }, ...xs].slice(0, MAX)));
    setLive((s) => (s.has(id) ? s : new Set(s).add(id)));
  }, []);
  const dismiss = useCallback((id: number) => setList((xs) => xs.filter((x) => x.id !== id)), []);
  const finish = useCallback((id: number, at: number) => {
    setList((xs) => xs.map((x) => (x.id === id && x.done === undefined ? { ...x, done: at } : x)));
  }, []);

  useEffect(() => {
    running?.forEach((j) => add(j.id));
  }, [running, add]);

  // задачи, закончившиеся без вас (панель не видела финала), не показываем — о них было уведомление
  const ids = list
    .filter((x) => live.has(x.id) || x.done !== undefined)
    .map((x) => x.id)
    .sort((a, b) => b - a);
  return { ids, live, add, dismiss, finish };
}

/* ---------- панель задачи ---------- */
export function AutoJobPanel({
  id,
  defaultOpen,
  onDismiss,
  onRetried,
  onFinished,
}: {
  id: number;
  defaultOpen: boolean;
  onDismiss: () => void;
  onRetried: (jobId: number) => void;
  onFinished?: (finishedAt: number) => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const feedId = useId();
  const [open, setOpen] = useState(defaultOpen);
  const [retrying, setRetrying] = useState(false);
  const sawRunning = useRef(false);
  const feedRef = useRef<HTMLDivElement>(null);

  const { job, events } = useJobStream(id, (j) => {
    onFinished?.(j.finished_at ?? Date.now());
    // список постов и статус обновляет общий поток; здесь — на случай, если он переподключается
    if (!sawRunning.current) return;
    void qc.invalidateQueries({ queryKey: ["posts"] });
    void qc.invalidateQueries({ queryKey: ["auto-status"] });
  });
  const running = !job || job.status === "running";
  useEffect(() => {
    if (job?.status === "running") sawRunning.current = true;
  }, [job?.status]);

  const elapsed = useElapsed(job?.started_at, running);
  const steps = events.filter((e) => e.kind !== "done");
  const current = steps.at(-1);
  const done = job?.status === "done";
  const failed = job?.status === "failed";

  const detail = useQuery({
    queryKey: ["jobs", "detail", id],
    queryFn: () => api.get<JobDetail>(`/api/jobs/${id}`),
    enabled: done,
    staleTime: 60_000,
  });

  // лента прокручивается за новыми шагами, если её не листали вверх
  useEffect(() => {
    const el = feedRef.current;
    if (!el || !open) return;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 120) el.scrollTop = el.scrollHeight;
  }, [events.length, open]);

  const retry = async () => {
    setRetrying(true);
    try {
      const r = await api.post<{ jobId: number }>(`/api/jobs/${id}/retry`);
      onRetried(r.jobId);
      toast("Задача запущена заново", "info");
    } catch (e) {
      toast(errText(e), "error");
    } finally {
      setRetrying(false);
    }
  };

  const state = running ? "running" : done ? "done" : "failed";
  const sub = running ? (current?.title ?? "Запускаю…") : (job?.info ?? "");
  const posts = detail.data?.posts ?? [];

  return (
    <section className={`auto-job auto-job--${state}`} aria-label={job ? `Задача: ${job.title}` : "Задача Claude"}>
      <div className="auto-job__head">
        <span className="auto-job__mark">
          {running ? (
            <EcgLoader size={56} />
          ) : done ? (
            <CircleCheck size={20} strokeWidth={2} aria-label="Готово" />
          ) : (
            <TriangleAlert size={20} strokeWidth={2} aria-label="Ошибка" />
          )}
        </span>
        <div className="auto-job__main">
          <h3 className="auto-job__title">{job?.title ?? "Подключаюсь к задаче"}</h3>
          <p className="auto-job__now" key={running ? (current?.id ?? 0) : state} aria-live={running ? "polite" : undefined}>
            {sub}
          </p>
        </div>
        <span className="auto-job__time">
          {running ? duration(elapsed) : job?.duration_ms != null ? `за ${duration(job.duration_ms)}` : ""}
        </span>
        <button
          className="btn btn--ghost btn--small auto-job__toggle"
          onClick={() => setOpen((x) => !x)}
          aria-expanded={open}
          aria-controls={feedId}
        >
          {open ? "Свернуть" : steps.length ? `Все шаги: ${steps.length}` : "Ход работы"}
          <ChevronDown size={16} className={open ? "is-open" : ""} aria-hidden="true" />
        </button>
        {!running && (
          <button className="icon-btn auto-job__dismiss" onClick={onDismiss} aria-label="Скрыть панель задачи" title="Скрыть">
            <X size={18} />
          </button>
        )}
      </div>

      {open && (
        <div className="auto-job__feed" id={feedId} ref={feedRef}>
          <JobFeed job={job} events={events} />
        </div>
      )}

      {!running && job && (
        <div className="auto-job__foot">
          {done &&
            (posts.length > 0 ? (
              <ul className="auto-job__posts" aria-label="Созданные посты">
                {posts.map((p) => (
                  <li key={p.id}>
                    <Link className="auto-job__post" to={`/posts/${p.id}`}>
                      <FileText size={15} aria-hidden="true" />
                      <span>{p.title}</span>
                    </Link>
                    {p.status !== "draft" && <StatusChip status={p.status} />}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="muted small auto-job__none">
                {detail.isLoading ? "Загружаю посты…" : job.post_ids.length ? "Посты из этой задачи уже удалены." : "Новых постов нет."}
              </p>
            ))}
          {failed && (
            <button className="btn btn--small" onClick={() => void retry()} disabled={retrying}>
              <RotateCcw size={15} aria-hidden="true" />
              {retrying ? "Запускаю…" : "Повторить"}
            </button>
          )}
        </div>
      )}
    </section>
  );
}
